/**
 * 简化的基础管理器类
 * 提供基本的生命周期管理
 */

import logger from '@/utils/logger';

// 管理器状态枚举
export enum ManagerState {
  IDLE = 'idle',
  INITIALIZING = 'initializing',
  READY = 'ready',
  ERROR = 'error',
  DESTROYED = 'destroyed',
}

// 简化的管理器配置接口
export interface BaseManagerConfig {
  name: string;
  autoInit?: boolean;
}

/**
 * 简化的基础管理器抽象类
 */
export abstract class BaseManager {
  protected config: Required<BaseManagerConfig>;

  protected state: ManagerState = ManagerState.IDLE;

  constructor(config: BaseManagerConfig) {
    this.config = {
      autoInit: true,
      ...config,
    };

    logger.debug(`[BaseManager] 创建管理器: ${this.config.name}`);

    // 自动初始化
    if (this.config.autoInit) {
      this.init().catch((error) => {
        logger.error(`[BaseManager] 自动初始化失败: ${this.config.name}`, error);
        this.state = ManagerState.ERROR;
      });
    }
  }

  /**
   * 初始化管理器
   */
  async init(): Promise<void> {
    if (this.state !== ManagerState.IDLE) {
      logger.warn(`[BaseManager] 管理器已初始化: ${this.config.name}`);
      return;
    }

    this.state = ManagerState.INITIALIZING;

    try {
      await this.onInit();
      this.state = ManagerState.READY;
      logger.info(`[BaseManager] 管理器初始化完成: ${this.config.name}`);
    } catch (error) {
      this.state = ManagerState.ERROR;
      logger.error(`[BaseManager] 管理器初始化失败: ${this.config.name}`, error);
      throw error;
    }
  }

  /**
   * 销毁管理器
   */
  async destroy(): Promise<void> {
    if (this.state === ManagerState.DESTROYED) {
      logger.warn(`[BaseManager] 管理器已销毁: ${this.config.name}`);
      return;
    }

    try {
      await this.onDestroy();
      this.state = ManagerState.DESTROYED;
      logger.info(`[BaseManager] 管理器已销毁: ${this.config.name}`);
    } catch (error) {
      logger.error(`[BaseManager] 管理器销毁失败: ${this.config.name}`, error);
      throw error;
    }
  }

  /**
   * 获取当前状态
   */
  getState(): ManagerState {
    return this.state;
  }

  /**
   * 检查是否就绪
   */
  isReady(): boolean {
    return this.state === ManagerState.READY;
  }

  /**
   * 检查是否已销毁
   */
  isDestroyed(): boolean {
    return this.state === ManagerState.DESTROYED;
  }

  /**
   * 获取配置
   */
  getConfig(): Required<BaseManagerConfig> {
    return this.config;
  }

  // 抽象方法，子类必须实现
  protected abstract onInit(): Promise<void>;

  protected abstract onDestroy(): Promise<void>;
}
